<script setup lang="ts">
import { getAudienceColor } from '@/composables/audiences'

import type { Audience } from '@/types/types'
import type { Icons } from '@/components/base/BaseIcon.vue'
import type { ChipColor } from '@/components/base/BaseChip.vue'

const { t } = useI18n()
const { segmentTypes } = useAudience()

const props = withDefaults(
  defineProps<{
    audiences: Audience[]
    hideEmptyCategories?: boolean
    defaultText?: string
    defaultFilterPlaceholder?: string
    defaultEmptyFilterMessage?: string
    defaultEmptyMessage?: string
    maxShownItems?: number
    disabled?: boolean
  }>(),
  {
    hideEmptyCategories: false,
    defaultText: '',
    defaultFilterPlaceholder: '',
    defaultEmptyFilterMessage: '',
    defaultEmptyMessage: '',
    maxShownItems: 5,
    disabled: false
  }
)

const modelValue = defineModel<number[]>('modelValue')

const emit = defineEmits<{
  (e: 'update:modelValue', value: number[]): void
}>()

const formattedAudiences = computed(() => {
  const typologyAudiences = sortAlphabetically(
    allDropdownValues.value.filter((audience) => audience.type === 'typology')
  )
  const dynamicAudiences = sortAlphabetically(allDropdownValues.value.filter((audience) => audience.type === 'dynamic'))
  const staticAudiences = sortAlphabetically(allDropdownValues.value.filter((audience) => audience.type === 'static'))

  const getFormattedCategory = (label: string, items: any[]) => {
    if (props.hideEmptyCategories && items.length === 0) {
      return []
    }
    return [
      {
        label,
        items
      }
    ]
  }

  return [
    ...getFormattedCategory(t('global.multiselect.audiences.typology'), typologyAudiences),
    ...getFormattedCategory(t('global.multiselect.audiences.dynamic'), dynamicAudiences),
    ...getFormattedCategory(t('global.multiselect.audiences.static'), staticAudiences)
  ]
    .filter((category) => category.items.length > 0)
    .flat()
})

const allDropdownValues = computed<
  {
    value: number
    label: string
    type: string
    color: ChipColor
    icon?: Icons
  }[]
>(() => {
  return props.audiences.map((audience) => {
    if (audience.frequency_type) {
      audience.type = 'typology'
    }

    return {
      value: audience.id,
      label: audience.name,
      type: segmentTypes.find((segment) => segment.id === audience.type)?.id,
      color: getAudienceColor(audience.type),
      icon: segmentTypes.find((segment) => segment.id === audience.type)?.icon
    }
  })
})

const sortAlphabetically = (values: any[]) => {
  return values.sort((a, b) => a.label.localeCompare(b.label))
}

const handleRemoveAudience = (value: number) => {
  const filteredAudienceIds = modelValue.value.filter((id) => id !== value)
  modelValue.value = filteredAudienceIds
  emit('update:modelValue', filteredAudienceIds)
}

const selectedItemsLabel = computed(() => {
  return modelValue.value.length
    ? t('global.multiselect.audiences.audiencesSelected', {
        count: modelValue.value.length
      })
    : props.defaultText || t('global.multiselect.audiences.selectSegment')
})

const placeholder = computed(() => {
  return props.defaultText || t('global.multiselect.audiences.selectSegment')
})

const filterPlaceholder = computed(() => {
  return props.defaultFilterPlaceholder || t('global.multiselect.audiences.segmentsSearchPlaceholder')
})

const emptyFilterMessage = computed(() => {
  return props.defaultEmptyFilterMessage || t('global.multiselect.audiences.noResultSearch')
})

const emptyMessage = computed(() => {
  return props.defaultEmptyMessage || t('global.multiselect.audiences.noResult')
})
</script>

<template>
  <BaseMultiSelect
    v-model="modelValue"
    :options="formattedAudiences"
    grouped
    :disabled="disabled"
    display="chip"
    fluid
    filter
    :show-toggle-all="false"
    :max-selected-labels="maxShownItems"
    :placeholder="placeholder"
    :selected-items-label="selectedItemsLabel"
    :filter-placeholder="filterPlaceholder"
    :empty-filter-message="emptyFilterMessage"
    :empty-message="emptyMessage"
  >
    <template #chip="{ value }">
      <BaseChip
        :text="audiences.find((audience) => audience.id === value)?.name"
        :removable="true"
        :color="allDropdownValues.find((audience) => audience.value === value)?.color"
        :icon="allDropdownValues.find((audience) => audience.value === value)?.icon"
        @click="$event.stopPropagation()"
        @remove="handleRemoveAudience(value)"
      />
    </template>

    <template #option="{ option }">
      <div class="flex items-center">
        <BaseChip :color="option.color" :text="option.label" :icon="option.icon" />
      </div>
    </template>

    <template #optiongroup="{ option }">
      <div class="mt-2 text-xs font-normal uppercase text-muted-foreground">{{ option.label }}</div>
    </template>
  </BaseMultiSelect>
</template>
